// components/PageTransition.js
// ─────────────────────────────────────────────────────────────────────────────
// Makes each page change feel like one event: fade to white, load what will be
// on screen while hidden, fade back in. Replaces pages assembling in stages
// (fonts, then images, then panels sliding in one after another).
//
// _layouts/main.html adds .is-loading in <head> before first paint, with its
// own timeout so a script failure can never leave a blank page.

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function decoded(img) {
  if (img.complete && img.naturalWidth) return Promise.resolve();
  const done = img.decode
    ? img.decode()
    : new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
  return done.catch(() => {}); // a broken image must not hold the page on white
}

export default class PageTransition {
  constructor({ fadeMs = 400, maxWaitMs = 2500 } = {}) {
    this.fadeMs = fadeMs;       // keep in step with the body transition in main.scss
    this.maxWaitMs = maxWaitMs; // never hold the page on white longer than this
    this.html = document.documentElement;
    this._onClick = this._onClick.bind(this);

    // The back/forward cache restores a page exactly as it was left — faded out.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) this.html.classList.remove('is-leaving', 'is-loading');
    });
  }

  /**
   * Resolve once fonts plus the images on screen inside `root` are ready
   * (capped). `root` may also be a single image to wait for just that one.
   */
  async ready(root = document) {
    const fold = window.innerHeight * 1.25;
    const images = root instanceof HTMLImageElement
      ? [root]
      : [...root.querySelectorAll('img')].filter((img) => {
          const r = img.getBoundingClientRect();
          return r.width > 0 && r.top < fold && r.bottom > 0;
        });
    // Lazy images won't start loading on their own while the page is hidden
    images.forEach((img) => { if (img.loading === 'lazy') img.loading = 'eager'; });

    const fonts = document.fonts ? document.fonts.ready.catch(() => {}) : Promise.resolve();
    await Promise.race([Promise.all([fonts, ...images.map(decoded)]), wait(this.maxWaitMs)]);
  }

  /** Warm the cache for images that aren't on screen yet (capped). */
  async preload(images) {
    const jobs = images.map((el) => {
      const src = typeof el === 'string' ? el : (el.currentSrc || el.getAttribute('src'));
      if (!src) return Promise.resolve();
      const img = new Image();
      img.src = src;
      return decoded(img);
    });
    await Promise.race([Promise.all(jobs), wait(this.maxWaitMs)]);
  }

  /** Fade in from white. */
  async reveal() {
    this.html.classList.remove('is-loading', 'is-leaving');
    await wait(this.fadeMs);
  }

  /** Fade out to white. */
  async cover() {
    this.html.classList.add('is-leaving');
    await wait(this.fadeMs);
  }

  /** Fade to white before following same-site links that load a new page. */
  bindLinks() {
    // On window rather than document so it runs after the document-level
    // handlers (CardFocus, ColumnDeck) and can tell whether they took the click.
    window.addEventListener('click', this._onClick);
  }

  _onClick(e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const link = e.target.closest?.('a[href]');
    if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;

    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin) return;
    if (url.pathname === location.pathname && url.search === location.search) return; // same page or anchor

    e.preventDefault();
    this.cover().then(() => location.assign(url.href));
  }
}
